import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";

const EMPTY = {
  name: "", role: "", badge: "", photo_url: "", categories: [], presentation: "",
  audience: "", buttons: [{ label: "", url: "", kind: "primary", icon: "" }],
  featured: false, order: 100, published: true,
};

export default function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/partners"); setPartners(data.partners); setCats(data.categories); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm({ ...EMPTY, buttons: [{ label: "", url: "", kind: "primary", icon: "" }] }); };
  const openEdit = (p) => {
    setEditingId(p.partner_id);
    setForm({ ...EMPTY, ...p, audience: (p.audience || []).join("\n"), buttons: p.buttons?.length ? p.buttons : [{ label: "", url: "", kind: "primary", icon: "" }] });
  };

  const toggleCat = (key) => setForm((f) => ({ ...f, categories: f.categories.includes(key) ? f.categories.filter((c) => c !== key) : [...f.categories, key] }));
  const setBtn = (i, k, v) => setForm((f) => ({ ...f, buttons: f.buttons.map((b, idx) => idx === i ? { ...b, [k]: v } : b) }));
  const addBtn = () => setForm((f) => ({ ...f, buttons: [...f.buttons, { label: "", url: "", kind: "secondary", icon: "" }] }));
  const rmBtn = (i) => setForm((f) => ({ ...f, buttons: f.buttons.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name) return toast.error("Le nom est requis.");
    const payload = {
      ...form, order: Number(form.order) || 100,
      audience: form.audience.split("\n").map((a) => a.trim()).filter(Boolean),
      buttons: form.buttons.filter((b) => b.label && b.url),
    };
    try {
      if (editingId) await api.put(`/admin/partners/${editingId}`, payload);
      else await api.post("/admin/partners", payload);
      toast.success(editingId ? "Partenaire mis à jour." : "Partenaire ajouté.");
      setForm(null); setEditingId(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce partenaire ?")) return; try { await api.delete(`/admin/partners/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const inputCls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2 outline-none text-sm";
  const Field = ({ label, children }) => (<label className="block"><span className="text-white/60 text-xs mb-1 block">{label}</span>{children}</label>);

  if (form) {
    return (
      <div className="card-surface p-5 max-w-3xl" data-testid="partners-admin-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg">{editingId ? "Modifier le partenaire" : "Nouveau partenaire"}</h3>
          <button onClick={() => setForm(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <Field label="Nom"><input data-testid="partners-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
          <Field label="Fonction"><input data-testid="partners-form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} /></Field>
          <Field label="Badge"><input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className={inputCls} placeholder="🤝🏿 Partenaire de BIZARRE AI" /></Field>
          <Field label="URL de la photo"><input data-testid="partners-form-photo" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className={inputCls} placeholder="/partners/nom.webp ou https://..." /></Field>
        </div>

        <span className="text-white/60 text-xs mb-1 block">Catégories</span>
        <div className="flex flex-wrap gap-2 mb-3">
          {cats.map((c) => (
            <button key={c.key} type="button" data-testid={`partners-form-cat-${c.key}`} onClick={() => toggleCat(c.key)}
              className={`text-sm rounded-full px-3 py-1.5 border ${form.categories.includes(c.key) ? "border-gold/50 bg-gold/10 text-gold" : "border-white/12 text-white/60"}`}>
              {c.emoji} {c.label}
            </button>
          ))}
        </div>

        <Field label="Présentation (séparez les paragraphes par une ligne vide)"><textarea rows={5} value={form.presentation} onChange={(e) => setForm({ ...form, presentation: e.target.value })} className={`${inputCls} resize-none mb-3`} /></Field>
        <Field label="Public concerné (une ligne par point)"><textarea rows={4} value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className={`${inputCls} resize-none mb-3`} placeholder={"Les jeunes victimes de harcèlement\nLes parents..."} /></Field>

        <span className="text-white/60 text-xs mb-1 block">Boutons</span>
        <div className="space-y-2 mb-3">
          {form.buttons.map((b, i) => (
            <div key={i} className="flex flex-wrap gap-2 items-center">
              <input value={b.label} onChange={(e) => setBtn(i, "label", e.target.value)} placeholder="Libellé" className={`${inputCls} flex-1 min-w-[120px]`} />
              <input value={b.url} onChange={(e) => setBtn(i, "url", e.target.value)} placeholder="https://..." className={`${inputCls} flex-1 min-w-[150px]`} />
              <select value={b.icon} onChange={(e) => setBtn(i, "icon", e.target.value)} className="bg-white/[0.06] border border-white/15 rounded-xl px-2 py-2 text-sm">
                <option value="">Icône</option><option value="globe">🌐 Site</option><option value="calendar">📅 RDV</option><option value="instagram">📷 Instagram</option><option value="tiktok">🎵 TikTok</option>
              </select>
              <select value={b.kind} onChange={(e) => setBtn(i, "kind", e.target.value)} className="bg-white/[0.06] border border-white/15 rounded-xl px-2 py-2 text-sm">
                <option value="primary">Principal</option><option value="secondary">Secondaire</option>
              </select>
              <button type="button" onClick={() => rmBtn(i)} className="text-white/40 hover:text-red-400"><X size={16} /></button>
            </div>
          ))}
          <button type="button" onClick={addBtn} className="text-gold text-sm inline-flex items-center gap-1"><Plus size={14} /> Ajouter un bouton</button>
        </div>

        <div className="flex flex-wrap gap-4 items-center mb-4">
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Mis en avant</label>
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} /> Publié</label>
          <Field label="Ordre"><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={`${inputCls} w-24`} /></Field>
        </div>
        <button data-testid="partners-form-save" onClick={save} className="gold-gradient text-black rounded-full px-6 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Save size={16} /> Enregistrer</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-white/40 text-xs">{partners.length} partenaire(s)</p>
        <button data-testid="partners-admin-new" onClick={openCreate} className="gold-gradient text-black rounded-xl px-4 py-2 text-sm font-medium inline-flex items-center gap-1.5"><Plus size={16} /> Nouveau partenaire</button>
      </div>
      <div className="space-y-2">
        {partners.map((p) => (
          <div key={p.partner_id} data-testid={`partners-admin-row-${p.partner_id}`} className="card-surface p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              {p.photo_url && <img src={p.photo_url} alt="" className="w-10 h-10 object-cover rounded-lg shrink-0" />}
              <div className="min-w-0">
                <p className="text-sm truncate">{p.name} {!p.published && <span className="text-white/40 text-xs">(brouillon)</span>}</p>
                <p className="text-white/35 text-xs mt-0.5 truncate">{p.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(p)} className="text-white/50 hover:text-gold"><Pencil size={16} /></button>
              <button onClick={() => del(p.partner_id)} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {partners.length === 0 && <p className="text-white/30 text-sm">Aucun partenaire.</p>}
      </div>
    </div>
  );
}
